<template>
  <div class="band-wrapper" style="width:100%;height:100%;">
    <span class="loading" v-if="loading"
      ><span class="load open"></span>{{ loadingPercent }}%</span
    >
    <div ref="canvas" class="band-container" :class="{ show: !loading }"></div>
  </div>
</template>

<script>
import "three/examples/js/loaders/LoaderSupport";
import "three/examples/js/loaders/MTLLoader";
import "three/examples/js/loaders/OBJLoader2";
import "three/examples/js/controls/OrbitControls";

import {
  OBJLoader2,
  OrbitControls,
  Scene,
  PerspectiveCamera,
  Box3,
  Vector3,
  Mesh,
  TextureLoader,
  MeshLambertMaterial,
  WebGLRenderer,
  RepeatWrapping,
  Object3D,
  DirectionalLight,
  AmbientLight,
  HemisphereLight
} from "three";

export default {
  props: {
    options: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      obj_3d: null,
      scene: null,
      camera: null,
      renderer: null,
      container: null,
      width: null,
      height: null,
      objName: "/assets/3d/Blackband_export3.obj",
      mtlName: "/assets/3d/Blackband_export3.mtl",
      rotating: true,
      loadingPercent: 0,
      loading: true,
      textures: [
        "/assets/3d/texture1.png",
        "/assets/3d/texture2.png",
        "/assets/3d/texture3.png"
      ]
    };
  },

  methods: {
    load() {
      return new Promise(resolve => {
        this.initObj(resolve);
      });
    },

    initObj(callback) {
      var vue = this;

      vue.container = vue.$refs.canvas;
      vue.width = vue.container.clientWidth;
      vue.height = vue.container.clientHeight;
      var aspect = vue.width / vue.height;
      vue.obj_3d = new Object3D();
      vue.scene = new Scene();
      vue.camera = new PerspectiveCamera(50, aspect, 0.1, 1000);
      vue.renderer = new WebGLRenderer({ antialias: true, alpha: true });
      vue.controls = new OrbitControls(vue.camera, vue.renderer.domElement);
      vue.container.addEventListener("mouseenter", function() {
        vue.rotating = false;
      });

      vue.container.addEventListener("mouseleave", () => (vue.rotating = true));

      vue.loading = true;
      vue.camera.position.z = 4;
      vue.scene.add(vue.camera);

      vue.renderer.setClearColor(0x000000, 0);
      vue.renderer.setSize(vue.width, vue.height);

      vue.container.appendChild(vue.renderer.domElement);

      vue.controls.target.set(0, 0, 0);

      if (vue.options.limitAngle) {
        vue.controls.minPolarAngle = vue.options.minPolar;
        vue.controls.maxPolarAngle = vue.options.maxPolar;
        vue.controls.minAzimuthAngle = vue.options.minAzimuth;
        vue.controls.maxAzimuthAngle = vue.options.maxAzimuth;
      } else {
        vue.controls.minPolarAngle = Math.PI / 3;
        vue.controls.maxPolarAngle = Math.PI / 1.5;
      }

      vue.controls.dampingFactor = 0.1;
      vue.controls.rotateSpeed = 0.1;

      vue.controls.enableKeys = false;
      vue.controls.enablePan = false;
      vue.controls.enableZoom = false;

      //set lights
      let lightLeft = new DirectionalLight(0x666666, 1);
      lightLeft.position.z = 10;
      lightLeft.position.x = -50;
      lightLeft.position.y = -10;
      vue.camera.add(lightLeft);

      //set lights
      let lightRight = new DirectionalLight(0x666666, 1);
      lightRight.position.z = 10;
      lightRight.position.x = 50;
      lightRight.position.y = -10;
      vue.camera.add(lightRight);

      let lightFront = new DirectionalLight(0xbbbbbb, 0.5);
      lightFront.position.z = 10;
      lightFront.position.x = 0;
      lightFront.position.y = 5;
      vue.camera.add(lightFront);

      let lightAbove = new HemisphereLight(0xffffff, 0xffffff, 0.5);
      vue.camera.add(lightAbove);

      let ambient = new AmbientLight(0x555555, 1);
      vue.scene.add(ambient);

      var objLoader = new OBJLoader2();

      objLoader.setLogging(false, false);

      var callbackOnLoad = function(event) {
        vue.obj_3d.add(event.detail.loaderRootNode);
        vue.obj_3d.rotation.x = vue.options.rotationX;
        vue.obj_3d.rotation.y = vue.options.rotationY;
        vue.obj_3d.rotation.z = vue.options.rotationZ;

        vue.changeColor(0);

        //object boundery
        var boundingBox = new Box3();
        boundingBox.setFromObject(vue.obj_3d);
        var center_targer = boundingBox.getCenter(new Vector3());
        vue.controls.target = center_targer;

        vue.scene.add(vue.obj_3d);

        setTimeout(() => {
          vue.loading = false;
        }, 1000);
        vue.animate();

        callback();
      };

      var callbackOnProgress = function(xhr) {
        if ((xhr.loaded / xhr.total) * 100 <= 100) {
          vue.loadingPercent = Math.round((xhr.loaded / xhr.total) * 100);
        }
      };

      var onLoadMtl = function(materials) {
        objLoader.setModelName("upmood-band");
        objLoader.setMaterials(materials);
        objLoader.load(
          vue.objName,
          callbackOnLoad,
          callbackOnProgress,
          null,
          null,
          true
        );
      };

      objLoader.loadMtl(vue.mtlName, null, onLoadMtl);
    },

    animate() {
      this.rendering_id = requestAnimationFrame(this.animate);
      if (this.obj_3d != null) {
        this.camera.aspect =
          this.container.clientWidth / this.container.clientHeight;
        this.renderer.setSize(
          this.container.clientWidth,
          this.container.clientHeight
        );

        this.camera.updateProjectionMatrix();

        if (this.options.rotate && this.rotating) {
          this.obj_3d.rotation.y += 0.002;
        }

        this.renderer.render(this.scene, this.camera);
        this.controls.update();
      }
    },

    stop() {
      cancelAnimationFrame(this.rendering_id);
    },

    changeColor(color) {
      var vue = this;
      vue.obj_3d.traverse(child => {
        if (child instanceof Mesh && child.name.indexOf("Cylinder") !== -1) {
          new TextureLoader().load(vue.textures[color], texture => {
            texture.wrapS = RepeatWrapping;
            texture.wrapT = RepeatWrapping;
            child.material = new MeshLambertMaterial({
              map: texture,
              needsUpdate: true
            });
          });
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.band-wrapper {
  position: relative;
}

.band-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: grab;
  opacity: 0;
  transition: opacity 0.5s ease;

  &.show {
    opacity: 1;
  }
}

.loading {
  width: 100px !important;
  height: 100px !important;
  display: flex !important;
  justify-content: center !important;
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  color: #83dcca !important;

  .load {
    border: 20px solid #83dcca !important;
    width: 100px !important;
    height: 100px !important;
    position: absolute !important;
  }

  .open {
    border-top: 20px solid transparent !important;
  }
}
</style>
